import { useMutation, UseMutationOptions } from "@tanstack/react-query";
import axiosInstance from "axiosConfig";
import { Toast } from "shared";
import { TOAST_TYPES_ENUM } from "enums";
import { DiscountValidationResponse } from "types/DiscountType";

interface ValidateDiscountCodeParams {
  code: string;
}

export default function useValidateDiscountCode(
  options?: UseMutationOptions<DiscountValidationResponse, Error, ValidateDiscountCodeParams>
) {
  let mutationStartTime = 0;
  const MIN_LOADING_TIME = 800;

  const waitForMinLoading = async () => {
    const elapsed = Date.now() - mutationStartTime;
    if (elapsed < MIN_LOADING_TIME) {
      await new Promise((r) => setTimeout(r, MIN_LOADING_TIME - elapsed));
    }
  };

  return useMutation({
    mutationKey: ["validateDiscountCode"],
    mutationFn: async (params: ValidateDiscountCodeParams) => {
      try {
        const response = await axiosInstance.post<DiscountValidationResponse>(
          "/discount-codes/validate",
          params
        );
        await waitForMinLoading();
        return response.data;
      } catch (error: any) {
        if (error?.response?.status === 400 && error?.response?.data?.valid === false) {
          await waitForMinLoading();
          return error.response.data;
        }
        await waitForMinLoading();
        throw error;
      }
    },
    onMutate: (variables) => {
      mutationStartTime = Date.now();
      Toast({
        message: "Sprawdzanie kodu...",
        type: TOAST_TYPES_ENUM.LOADING,
        id: "validateDiscountCode",
      });
      if (options && options.onMutate) {
        return options.onMutate(variables);
      }
    },
    onSuccess: (data, variables, context) => {
      if (data.valid && data.discount) {
        Toast({
          type: TOAST_TYPES_ENUM.SUCCESS,
          message: data.message,
          id: "validateDiscountCode",
        });
      } else {
        Toast({
          type: TOAST_TYPES_ENUM.SUCCESS,
          message: data.message,
          id: "validateDiscountCode",
        });
      }
      if (options && options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error: any, variables, context) => {
      Toast({
        type: TOAST_TYPES_ENUM.ERROR,
        message: error?.data?.message || "Wystąpił błąd podczas walidacji kodu",
        id: "validateDiscountCode",
      });
      if (options && options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
}
